import React from 'react';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { NextUI } from '@/components/NextUI';
import StatusCheck from '../EditShareModal.icons/StatusCheck';
import StatusGlowPink from './icons/StatusGlowPink';
import StatusGlowGreen from './icons/StatusGlowGreen';
import FocusLock from './icons/FocusLock';
import CloseCrossSmall from './icons/CloseCrossSmall';

type ProtectPdfDialogTone = 'lock' | 'success';

const ProtectPdfDialog = ({
  name,
  tone,
  title,
  description,
  descriptionClassName,
  onDismiss,
  showCloseIcon = false,
  isDismissable = true,
  isKeyboardDismissDisabled = false,
  children,
}: {
  name: string;
  tone: ProtectPdfDialogTone;
  title: string;
  description: string;
  descriptionClassName?: string;
  onDismiss: () => void;
  showCloseIcon?: boolean;
  isDismissable?: boolean;
  isKeyboardDismissDisabled?: boolean;
  children: React.ReactNode;
}) => {
  const t = useTranslations('protect_pdf_flow');
  const Glow = tone === 'success' ? StatusGlowGreen : StatusGlowPink;

  return (
    <NextUI.Modal
      className="mx-4 max-w-[470px] rounded-[16px] shadow-none"
      classNames={{
        content:
          'relative max-h-[calc(100dvh-32px)] overflow-x-hidden overflow-y-auto rounded-[16px] bg-white px-6 py-8 shadow-none md:px-12',
        backdrop: 'bg-[rgba(124,124,124,0.5)]',
      }}
      defaultOpen={true}
      name={name}
      backdrop="opaque"
      hideCloseButton={true}
      isDismissable={isDismissable}
      isKeyboardDismissDisabled={isKeyboardDismissDisabled}
      onClose={onDismiss}
    >
      <Glow className="pointer-events-none absolute left-0 top-0 h-[152px] w-full" />

      <div className="relative flex flex-col gap-8">
        <div className="flex flex-col items-center gap-4">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-white shadow-[0px_24px_32px_0px_rgba(0,0,0,0.08)]">
            {tone === 'success' ? (
              <StatusCheck className="h-10 w-10 text-[#31CA93]" />
            ) : (
              <FocusLock className="h-10 w-10 text-[#F43F5E]" />
            )}
          </div>

          <div className="flex w-full flex-col items-center gap-1 break-words text-center">
            <span className="w-full font-barlow text-[28px] font-semibold leading-[34px] text-[#454655]">
              {title}
            </span>
            <span
              className={clsx(
                'w-full font-barlow text-[17px] font-normal leading-[20px] text-[#787887]',
                descriptionClassName
              )}
            >
              {description}
            </span>
          </div>
        </div>

        {children}
      </div>

      {showCloseIcon ? (
        <button
          type="button"
          aria-label={t('close_aria')}
          onClick={onDismiss}
          className="absolute left-5 top-5 z-10 flex h-8 w-8 items-center justify-center text-[#454655] outline-none"
        >
          <CloseCrossSmall className="h-8 w-8" />
        </button>
      ) : null}
    </NextUI.Modal>
  );
};

export default ProtectPdfDialog;
